import { useCallback, useRef } from 'react';
import * as Tone from 'tone';
import { instrumentOptions, createEnhancedInstrument, createToneSynth } from '../utils/instrumentFactory';

export const useSynthManager = (audioHook) => {
  const { 
    partSynthsRef, 
    globalSynthConfigRef, 
    isPlaying,
    pendingDisposalsRef,
    getPartVolume,
    overrideVolumesEnabled,
    getOverrideVolume,
    isDisposingRef
  } = audioHook;

  // Track synth generations to avoid using stale synths
  const synthGenerationRef = useRef({});

  // Calculate final volume: master dB + percentage scaling
  const calculateFinalVolume = useCallback((partId) => {
    const masterVolumeDb = globalSynthConfigRef.current.volume; // Master volume in dB
    const partPercentage = overrideVolumesEnabled ? getOverrideVolume(partId) : getPartVolume(partId); // Part volume as percentage (0-100)
    
    // Convert percentage to dB offset: 100% = 0dB, 50% = -6dB, 0% = -40dB
    const percentageToDb = (partPercentage === 0) ? -40 : (20 * Math.log10(partPercentage / 100));
    
    const finalVolume = masterVolumeDb + percentageToDb;
    
    return finalVolume;
  }, [getPartVolume, getOverrideVolume, overrideVolumesEnabled, globalSynthConfigRef]);

  const createOrUpdatePartSynth = useCallback(async (partId, forceRecreate = false) => {
    // CRITICAL: Don't create synths during disposal
    if (isDisposingRef.current) {
      return;
    }

    // Prevent duplicate synth creation
    if (!forceRecreate && partSynthsRef.current[partId] && !partSynthsRef.current[partId]._disposed) {
      return;
    }

    if (Tone.context.state !== 'running') {
      await Tone.start();
    }

    const selectedInstrument = instrumentOptions.find(opt => opt.value === globalSynthConfigRef.current.typeName);
    const finalVolume = calculateFinalVolume(partId); // Calculate master + percentage
    
    
    // If we're playing, we need to handle this carefully
    if (isPlaying && partSynthsRef.current[partId]) {
      // Create a new synth without disposing the old one immediately
      let newSynth;
      
      if (selectedInstrument?.type === 'enhanced') {
        newSynth = await createEnhancedInstrument(
          globalSynthConfigRef.current.typeName, 
          finalVolume // Use calculated final volume
        );
      } else {
        const synthConfig = { ...globalSynthConfigRef.current, volume: finalVolume };
        newSynth = createToneSynth(synthConfig);
      }
      
      // Keep reference to old synth
      const oldSynth = partSynthsRef.current[partId];
      
      // Mark the old synth as disposed (even though we haven't actually disposed it yet)
      if (oldSynth) {
        oldSynth._disposed = true;
      }
      
      // Replace with new synth
      partSynthsRef.current[partId] = newSynth;
      
      // Increment generation
      synthGenerationRef.current[partId] = (synthGenerationRef.current[partId] || 0) + 1;
      
      // Schedule the old synth for disposal after a delay
      if (oldSynth) {
        const timeoutId = setTimeout(() => {
          try {
            if (oldSynth.releaseAll) {
              oldSynth.releaseAll();
            }
            oldSynth.dispose();
          } catch (e) {
          }
          
          // Remove this timeout from pending disposals
          const index = pendingDisposalsRef.current.indexOf(timeoutId);
          if (index > -1) {
            pendingDisposalsRef.current.splice(index, 1);
          }
        }, 2000); // 2 second delay to ensure notes finish
        
        pendingDisposalsRef.current.push(timeoutId);
      }
      
      return;
    }
    
    // Normal creation when not playing
    if (!forceRecreate && partSynthsRef.current[partId] && !partSynthsRef.current[partId]._disposed) {
      // Just update volume
      if (partSynthsRef.current[partId].volume) {
        partSynthsRef.current[partId].volume.value = finalVolume;
      }
      return;
    }
    
    // Dispose old synth if it exists
    if (partSynthsRef.current[partId]) {
      try {
        if (partSynthsRef.current[partId].releaseAll) {
          partSynthsRef.current[partId].releaseAll();
        }
        if (!partSynthsRef.current[partId]._disposed) {
          partSynthsRef.current[partId].dispose();
        }
      } catch (e) {
      }
    }
    
    // Create new synth with part-specific volume
    if (selectedInstrument?.type === 'enhanced') {
      partSynthsRef.current[partId] = await createEnhancedInstrument(
        globalSynthConfigRef.current.typeName, 
        finalVolume
      );
    } else {
      const synthConfig = { ...globalSynthConfigRef.current, volume: finalVolume };
      partSynthsRef.current[partId] = createToneSynth(synthConfig);
    }
    
    // Increment generation
    synthGenerationRef.current[partId] = (synthGenerationRef.current[partId] || 0) + 1;
    
  }, [partSynthsRef, globalSynthConfigRef, isPlaying, pendingDisposalsRef, calculateFinalVolume]);

  const syncAllPartSynths = useCallback((forceRecreateAll = false) => {
    const partIds = Object.keys(partSynthsRef.current);
    partIds.forEach(partId => {
      createOrUpdatePartSynth(partId, forceRecreateAll);
    });
  }, [createOrUpdatePartSynth, partSynthsRef]);

  const updateVolume = useCallback(() => {
    Object.entries(partSynthsRef.current).forEach(([partId, synth]) => {
      if (synth && synth.volume && !synth._disposed) {
        try {
          const finalVolume = calculateFinalVolume(partId);
          synth.volume.value = finalVolume;
        } catch (e) {
        }
      }
    });
  }, [partSynthsRef, calculateFinalVolume]);

  return {
    createOrUpdatePartSynth,
    syncAllPartSynths,
    updateVolume
  };
};